import { Fragment } from "react";
import { Star1 } from "iconsax-react";
import cn from "classnames";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";

export type StepsState = "pending" | "active" | "completed" | "error" | "star";

export interface StepsItem {
  id: string;
  label: string;
  description?: string;
  state?: StepsState;
}

interface Props {
  steps: StepsItem[];
  activeId: string;
  completedIds?: string[];
  errorIds?: string[];
  orientation?: "horizontal" | "vertical";
  onStepClick?: (id: string) => void;
  className?: string;
}

function resolveState(step: StepsItem, activeId: string, completedIds: string[], errorIds: string[]): StepsState {
  if (step.state) return step.state;
  if (errorIds.includes(step.id)) return "error";
  if (step.id === activeId) return "active";
  if (completedIds.includes(step.id)) return "completed";
  return "pending";
}

const CIRCLE_CLASSES: Record<StepsState, string> = {
  completed: "primary-focused-radial-gradient text-white",
  active: "primary-focused-radial-gradient text-white",
  error: "negative-focused-radial-gradient text-white border border-red-700",
  star: "surface-secondary-fill content-base-secondary",
  pending: "surface-secondary-fill content-base-secondary border surface-base-stroke",
};

function renderCircleContent(state: StepsState, index: number) {
  if (state === "completed") return <CheckIcon />;
  if (state === "error") return <CloseIcon />;
  if (state === "star") return <Star1 size={14} variant="Bold" color="currentColor" />;
  return index + 1;
}

function labelClass(state: StepsState): string {
  if (state === "active") return "text-body-bold-sm content-base-primary";
  if (state === "error") return "text-body-bold-sm content-action-negative";
  if (state === "completed") return "text-body-regular-sm content-base-primary";
  return "text-body-regular-sm content-base-secondary";
}

export default function Steps({
  steps,
  activeId,
  completedIds = [],
  errorIds = [],
  orientation = "horizontal",
  onStepClick,
  className,
}: Props) {
  const isHorizontal = orientation === "horizontal";

  function renderNode(step: StepsItem, index: number) {
    const state = resolveState(step, activeId, completedIds, errorIds);
    const isClickable = !!onStepClick && (state === "completed" || state === "active");

    const content = (
      <>
        <span
          className={cn(
            "w-7 aspect-square rounded-full flex items-center justify-center text-label-xs shrink-0",
            CIRCLE_CLASSES[state],
          )}>
          {renderCircleContent(state, index)}
        </span>
        <span className="flex flex-col gap-0.5 min-w-0">
          <span className={cn("truncate", labelClass(state))}>{step.label}</span>
          {step.description && (
            <span className="text-body-regular-xs content-base-low truncate">{step.description}</span>
          )}
        </span>
      </>
    );

    if (isClickable) {
      return (
        <button
          type="button"
          onClick={() => onStepClick?.(step.id)}
          className={cn("flex items-center gap-2 shrink-0 cursor-pointer", !isHorizontal && "items-start")}>
          {content}
        </button>
      );
    }

    return <div className={cn("flex items-center gap-2 shrink-0", !isHorizontal && "items-start")}>{content}</div>;
  }

  function renderConnector(step: StepsItem) {
    const isDone = step.state === "completed" || completedIds.includes(step.id);
    if (isHorizontal) {
      return <span className={cn("flex-1 h-px mx-2", isDone ? "background-brand-fill" : "bg-grey-200")} />;
    }
    return <span className={cn("w-px my-1 ml-3.5 h-6", isDone ? "background-brand-fill" : "bg-grey-200")} />;
  }

  return (
    <div className={cn(isHorizontal ? "flex items-center w-full" : "flex flex-col", className)}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <Fragment key={step.id}>
            {renderNode(step, index)}
            {!isLast && renderConnector(step)}
          </Fragment>
        );
      })}
    </div>
  );
}
